import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import ArcadeNav from "../arcade/ArcadeNav";
import ArcadeFooter from "../arcade/ArcadeFooter";
import useSEO from "../seo/useSEO";
import "./legal.css";

const CONTACT_EMAIL = "hello@zombiecollector3d.com";
const LAST_UPDATED = "June 26, 2026";

const GAMES = [
  { name: "Zombie Collector 3D",  blurb: "UFO tractor-beam action across 11 zombie worlds — the flagship 3D game." },
  { name: "Zombie Solitaire",     blurb: "Klondike with a graveyard twist — Brains, Blood, Tombstones, Bones instead of Hearts, Diamonds, Spades, Clubs." },
  { name: "Zombie Crush",         blurb: "7×7 match-3 with 6 zombie tile types, cascading combos, and match-4/5 power tiles." },
  { name: "Zombie Merge",         blurb: "Drag-to-merge zombies across 13 mutation tiers." },
  { name: "Zombie Idle Tycoon",   blurb: "Build a 14-facility zombie empire that earns gold even while you sleep." },
];

export default function AboutPage() {
  useSEO({
    title: "About — Zombie Arcade",
    description:
      "Zombie Arcade is a free HTML5 game portal hosting five original zombie-themed games. Learn how we build, what we promise, and how we make money.",
    canonical: "https://zombiecollector3d.com/about",
  });

  return (
    <div className="legal-page" data-testid="about-page">
      <ArcadeNav />
      <main className="legal-main">
        <Link to="/" className="legal-back" data-testid="about-back">
          <ArrowLeft size={16} /> Back to Arcade
        </Link>

        <header className="legal-hero">
          <h1>About Zombie Arcade</h1>
          <p className="subtitle">
            Five original zombie games. One free portal. No pop-ups, no
            paywalls, no dark patterns.
          </p>
          <p className="legal-updated">Last updated: {LAST_UPDATED}</p>
        </header>

        <section className="legal-section">
          <h2>What Zombie Arcade is</h2>
          <p>
            Zombie Arcade is a free browser-based game portal built around
            one weird question: <em>&quot;What if every classic casual-game
            genre had a zombie version?&quot;</em> We ship one original zombie
            game per genre &mdash; 3D action, solitaire, match-3, merge, and
            idle tycoon &mdash; all under one login, one shared coin
            economy, and one universal art style.
          </p>
          <p>
            Every game is playable instantly in your browser on desktop
            or mobile. No download, no install, no signup required to
            play. Sign in with Google if you want your progress to follow
            you across devices and earn Arcade Coins you can spend
            anywhere on the Site.
          </p>
        </section>

        <section className="legal-section">
          <h2>The five games</h2>
          <div className="about-games">
            {GAMES.map((g) => (
              <div key={g.name} className="about-game">
                <h4>{g.name}</h4>
                <p>{g.blurb}</p>
              </div>
            ))}
          </div>
          <p style={{ marginTop: 18 }}>
            More games are on the way &mdash; Zombie Tower Defense is
            currently in production, with Zombie Farm and a Zombie
            Roguelike coming after that.
          </p>
        </section>

        <section className="legal-section">
          <h2>What we promise</h2>
          <ul>
            <li><strong>Every game plays free.</strong> The full game is available at no cost, forever.</li>
            <li><strong>No pay-to-win.</strong> VIP unlocks convenience (extra upgrade choices, daily crates, no ads) and cosmetics &mdash; never raw power that free players can&apos;t match.</li>
            <li><strong>No dark patterns.</strong> No fake &quot;last chance&quot; timers, no manufactured scarcity, no shady auto-renewing subscriptions. VIP passes are one-time purchases with a clear end date.</li>
            <li><strong>Your data is yours.</strong> Email us and we&apos;ll export or delete your account within 7 days.</li>
            <li><strong>We ship in the open.</strong> Bugs get fixed weekly. New features get shipped whenever they&apos;re ready.</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>How we make money</h2>
          <p>
            Zombie Arcade is funded by three things, in order of size:
          </p>
          <ol>
            <li>
              <strong>Optional VIP passes</strong> ($0.99 weekly, $2.99
              monthly, $19.99 lifetime, $29.99 Founder pack) processed by
              Stripe.
            </li>
            <li>
              <strong>Optional coin packs</strong> for players who&apos;d rather
              buy currency than grind. Purely a convenience &mdash; every
              coin is also earnable in-game.
            </li>
            <li>
              <strong>Ads</strong>: we&apos;re approved for Google&apos;s H5 Games
              Ads program but haven&apos;t enabled ads yet. When we do, VIP
              and Founder players will be completely ad-free, and free
              players will only see opt-in rewarded video (watch to earn
              coins, revives, etc.) &mdash; never intrusive pop-ups.
            </li>
          </ol>
        </section>

        <section className="legal-section">
          <h2>The tech</h2>
          <p>
            Zombie Arcade is built by a small independent team. The stack
            is React 19 + Three.js (React Three Fiber for the 3D game) on
            the front end, FastAPI + MongoDB on the back end, Stripe for
            payments, and Emergent for hosting. All the game art is
            hand-drawn and iterated on with generative-AI tools; every
            asset is reviewed and edited before it ships. All the game
            code, level design, and balance is written by humans.
          </p>
        </section>

        <section className="legal-section">
          <h2>Get in touch</h2>
          <p>
            Bug reports, feature requests, love letters, hate mail, or
            just to say hi &mdash;{" "}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. We
            read every email and reply within a few days.
          </p>
        </section>

        <ArcadeFooter />
      </main>
    </div>
  );
}
